import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SortConfig } from '../../core/interface/SortConfig';
import { PaginationConfig } from '../../core/interface/PaginationConfig';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { SalesActivitiesService } from '../../core/services/sales-activities.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SalesContactInfo, SalesContactInfoResponse, SalesDetail, SalesDetailList, SalesDetailResponse, SalesFinanceInfo, SalesFinanceInfoResponse } from '../../core/models/sales';
import { DetailSales } from '../../core/models/residences';

@Component({
  selector: 'app-sales-activities-detail',
  templateUrl: './sales-activities-detail.component.html',
  styleUrl: './sales-activities-detail.component.css'
})


export class SalesActivitiesDetailComponent {

  itemSalesList: SalesDetailList[] = [];
  SalesDetail: SalesDetail | null = null;
  SalesContactInfo: SalesContactInfo[] = [];
  ItemSalesFinance: SalesFinanceInfo[] = [];
  isLoading = false;
  error = '';

  // Filters
  searchForm !: FormGroup;
  sortBy = '';
  startDate: string = '';
  endDate: string = ''

  sortConfig: SortConfig = {
    column: '',
    direction: ''
  };

  paginationConfig: PaginationConfig = {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0
  };

  searchTerm: string = '';

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  showFloorDetails: boolean = false;


  constructor(
    private salesServices: SalesActivitiesService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {

    this.setupSearchSubscription();

    this.searchForm = this.fb.group({
      searchTerm: ['']
    });
  }


  private setupSearchSubscription(): void {
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Update URL dan load data hanya jika ada term
      if (term.trim()) {
        this.updateUrlParams();
      }
      this.loadSalesDetailList(term);
    });
  }

  ngOnInit(): void {

    const startDate = localStorage.getItem('startDate')
    const endDate = localStorage.getItem('endDate')

    this.startDate = startDate ? startDate : '';
    this.endDate = endDate ? endDate : '';

    this.loadDetailData();
    this.loadContactInfo();
    this.loadSalesFinanceInfo();


    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.searchTerm = params['search'] || '';
        this.sortConfig.column = params['sortColumn'] || '';
        this.sortConfig.direction = params['sortDirection'] as 'ASC' | 'DESC' | '' || '';
        this.paginationConfig.currentPage = Number(params['page']) || 1;

        this.loadSalesDetailList(this.searchTerm);
      });

    const paramsId = this.route.snapshot.paramMap.get('id');

    this.route.queryParams.subscribe(params => {
      if (paramsId) {
        this.showFloorDetails = true;
      }
    });
  }

  loadDetailData(): void {
    this.isLoading = true;
    const paramsId = this.route.snapshot.paramMap.get('id');
    this.salesServices.getSalesDetail(
      paramsId ? Number(paramsId) : null,
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response: SalesDetailResponse) => {
        this.SalesDetail = response.data;
        this.isLoading = false;
      },
      error: (error) => {
        this.error = error;
        this.isLoading = false;
        this.SalesDetail = {} as SalesDetail;
      }
    });
  }

  loadSalesFinanceInfo(): void {
    this.isLoading = true;

    // Ambil parameter yang dibutuhkan
    const id = this.getIdFromUrl();
    const filters = this.getFiltersFromStorage();

    // Panggil service dengan parameter yang sudah disiapkan
    this.salesServices.getSalesFinanceInfo(
      id,
      this.startDate,
      this.endDate,
      filters.tax,
      filters.revCategory,
      filters.priceEstimate
    ).subscribe({
      next: (response: SalesFinanceInfoResponse) => {
        this.ItemSalesFinance = response.data;
        this.isLoading = false;

        console.log(this.ItemSalesFinance)
      },
      error: (error) => {
        this.handleError(error);
      }
    });
  }

  // Helper method untuk ambil ID dari URL
  private getIdFromUrl(): number | null {
    const paramsId = this.route.snapshot.paramMap.get('id');
    return paramsId ? Number(paramsId) : null;
  }

  // Helper method untuk ambil dan parse data dari localStorage
  private getFiltersFromStorage() {
    return {
      tax: this.parseStorageValue('item_tax'),
      revCategory: this.parseStorageValue('item_rev_category'),
      priceEstimate: this.parseStorageValue('item_price')
    };
  }

  // Helper method untuk parse nilai dari localStorage
  private parseStorageValue(key: string): string {
    const value = localStorage.getItem(key);

    // Return '0' jika tidak ada value atau value adalah '0'
    if (!value || value === '0') {
      return '0';
    }

    try {
      const parsed = JSON.parse(value);
      return String(parsed);
    } catch (error) {
      console.warn(`Error parsing ${key} from localStorage:`, error);
      return '0';
    }
  }

  // Helper method untuk handle error
  private handleError(error: any): void {
    console.error('Error loading sales finance info:', error);
    this.error = error;
    this.isLoading = false;
    this.ItemSalesFinance = [];
  }

  loadContactInfo(): void {
    this.isLoading = true;
    const paramsId = this.route.snapshot.paramMap.get('id');
    this.salesServices.getSalesDetailContactInfo(
      paramsId ? Number(paramsId) : null,
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.SalesContactInfo = response.data;
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.error = error;
        this.isLoading = false;
        this.SalesContactInfo = [];
      }
    });
  }

  loadSalesDetailList(searchTerm?: string): void {
    this.isLoading = true;
    this.error = ''; // Reset error sebelumnya
    const paramsId = this.route.snapshot.paramMap.get('id');
    // console.log(paramsId);

    this.salesServices.getSalesDetailList(
      paramsId || '',
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage,
      searchTerm?.trim() || '',
      this.sortConfig.column,
      this.sortConfig.direction ? this.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC' : undefined,
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response) => {
        if (response && response.data) {
          // Memeriksa struktur respons sebelum mengassign
          this.itemSalesList = response.data;

          if (response.pagination) {
            this.paginationConfig.totalItems = response.pagination.total_items;
            this.paginationConfig.currentPage = response.pagination.current_page;
          }

          this.isLoading = false;
        } else {
          this.error = 'Respons API tidak valid';
          this.isLoading = false;
          this.itemSalesList = [];
        }
      },
      error: (error) => {
        let errorMessage = 'Error saat memuat data residences: ';

        if (error.status === 200 && error.statusText === 'OK') {
          errorMessage += 'Error saat memproses format respons JSON';
        } else if (error.error instanceof ErrorEvent) {
          // Error dari sisi client
          errorMessage += `Error client: ${error.error.message}`;
        } else {
          // Error dari sisi server
          errorMessage += `Kode: ${error.status}, Pesan: ${error.message}`;
        }

        console.error(errorMessage, error);
        this.error = errorMessage;
        this.isLoading = false;
        this.itemSalesList = [];
      }
    });
  }

  onSort(column: string): void {
    if (this.sortConfig.column === column) {
      this.sortConfig.direction = this.sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortConfig.column = column;
      this.sortConfig.direction = 'ASC';
    }

    this.paginationConfig.currentPage = 1; // Reset to first page on sort
    this.updateUrlParams();
    this.loadSalesDetailList(this.searchTerm);
  }

  getSortIcon(column: string): string {
    // Jika bukan kolom yang sedang di-sort, tampilkan icon default
    if (this.sortConfig.column !== column) {
      return 'ri-expand-up-down-line'; // Remix icon untuk unsorted
    }

    // Jika kolom yang sedang di-sort
    if (this.sortConfig.direction === 'ASC') {
      return 'ri-arrow-up-line'; // Remix icon arrow up
    } else if (this.sortConfig.direction === 'DESC') {
      return 'ri-arrow-down-line'; // Remix icon arrow down
    }

    return 'ri-expand-up-down-line'; // Default fallback
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.paginationConfig.currentPage
    };

    // Hanya tambahkan search param jika ada nilai
    if (this.searchTerm?.trim()) {
      queryParams.search = this.searchTerm.trim();
    } else {
      queryParams.search = null; // Hapus search param
    }

    if (this.sortConfig.column && this.sortConfig.direction) {
      queryParams.sortColumn = this.sortConfig.column;
      queryParams.sortDirection = this.sortConfig.direction;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  get totalPages(): number {
    return Math.ceil(this.paginationConfig.totalItems / this.paginationConfig.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const currentPage = this.paginationConfig.currentPage;
    const totalPages = this.totalPages;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get displayRange(): { start: number; end: number; total: number } {
    const start = (this.paginationConfig.currentPage - 1) * this.paginationConfig.itemsPerPage + 1;
    const end = Math.min(
      start + this.paginationConfig.itemsPerPage - 1,
      this.paginationConfig.totalItems
    );

    return {
      start: this.paginationConfig.totalItems === 0 ? 0 : start,
      end,
      total: this.paginationConfig.totalItems
    };
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.paginationConfig.currentPage = 1;
    this.updateUrlParams();
    this.loadSalesDetailList('');
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.paginationConfig.currentPage) {
      return;
    }

    this.paginationConfig.currentPage = page;
    this.updateUrlParams();
    this.loadSalesDetailList(this.searchTerm);
  }


  onFirstPage(): void {
    this.onPageChange(1);
  }

  onLastPage(): void {
    this.onPageChange(this.totalPages);
  }


  onPrevPage(): void {
    this.onPageChange(this.paginationConfig.currentPage - 1);
  }

  onNextPage(): void {
    this.onPageChange(this.paginationConfig.currentPage + 1);
  }

}
